import { useParams, useLocation } from 'react-router-dom';
import { ReportCard } from '@molecules/ReportCard';
import { VerticalStepper, VerticalStepProps } from '@molecules/VerticalStepper';
import { BackLink } from '@molecules/BackLink/BackLink';
import {
  BACK_LINK_LABELS,
  REPORT_STATUS,
  REPORT_STATUS_COLORS,
  REPORT_WORKFLOW_STEP_LABELS,
  VERTICAL_STEP_STATUS,
  type ReportStatus,
  type VerticalStepStatus,
} from '../../../constants';
import { useReport } from '../../../hooks/useReport';
import { RoutesList } from '../../../routes/Routes';

const WORKFLOW_STEPS = [
  {
    label: REPORT_WORKFLOW_STEP_LABELS.SUBMITTED,
    matchStatus: REPORT_STATUS.RECEIVED,
  },
  {
    label: REPORT_WORKFLOW_STEP_LABELS.IN_PROGRESS,
    matchStatus: REPORT_STATUS.IN_PROGRESS,
  },
  {
    label: REPORT_WORKFLOW_STEP_LABELS.NEED_INFORMATION,
    matchStatus: REPORT_STATUS.NEED_INFORMATION,
  },
  {
    label: REPORT_WORKFLOW_STEP_LABELS.RESOLVED,
    matchStatus: REPORT_STATUS.RESOLVED,
  },
];

const CLOSED_WORKFLOW_STEPS = [
  {
    label: REPORT_WORKFLOW_STEP_LABELS.RECEIVED,
    matchStatus: REPORT_STATUS.RECEIVED,
  },
  {
    label: REPORT_WORKFLOW_STEP_LABELS.CLOSED,
    matchStatus: REPORT_STATUS.CLOSED,
  },
];

const getStepsForStatus = (reportStatus: string): VerticalStepProps[] => {
  const WORKFLOW =
    reportStatus === REPORT_STATUS.CLOSED
      ? CLOSED_WORKFLOW_STEPS
      : WORKFLOW_STEPS;
  const activeIndex = WORKFLOW.findIndex(
    (step) => step.matchStatus === reportStatus,
  );

  return WORKFLOW.map((step, index) => {
    const colors = REPORT_STATUS_COLORS[step.matchStatus as ReportStatus];
    const { bg, text } = colors ?? REPORT_STATUS_COLORS.Received;
    return {
      label: step.label,
      status: (index <= activeIndex
        ? VERTICAL_STEP_STATUS.COMPLETED
        : VERTICAL_STEP_STATUS.PENDING) as VerticalStepStatus,
      color: bg,
      tickColor: text,
    };
  });
};

export const ReportDetail = () => {
  const { state } = useLocation() as { state?: { previousPath?: string } };
  const { reportId } = useParams<{ reportId: string }>();
  const { data: report, isLoading, error } = useReport(reportId);

  const previousPath =
    state?.previousPath === RoutesList.HOME
      ? RoutesList.HOME
      : RoutesList.REPORTS_LIST;
  const previousPathLabel =
    previousPath === RoutesList.HOME
      ? BACK_LINK_LABELS.DASHBOARD
      : BACK_LINK_LABELS.REPORTS_LIST;
  const backLink = <BackLink to={previousPath} label={previousPathLabel} />;

  if (isLoading) {
    return (
      <div className="section-horizontal-padding">
        {backLink}
        <p className="text-body text-text-body mt-4">Loading report...</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="section-horizontal-padding">
        {backLink}
        <p className="text-body text-text-body mt-4">
          {error
            ? 'Failed to load report. Please try again later.'
            : 'Report not found.'}
        </p>
      </div>
    );
  }

  const steps = getStepsForStatus(report.status);

  return (
    <div className="section-horizontal-padding pb-60 md:pb-96">
      {backLink}
      <ReportCard
        id={report.id}
        title={report.title}
        caseReference={report.caseReference}
        dateCreated={report.dateCreated}
        dateUpdated={report.dateUpdated}
        status={report.status}
        colors={REPORT_STATUS_COLORS}
        hideLink
      />

      <VerticalStepper steps={steps} className="mt-12" />
    </div>
  );
};
